import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../../firebase';
import {
    collection, getDocs, query, where, getDoc, doc
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useReactToPrint } from 'react-to-print';

// MUI Components
import {
    Paper, Typography, TextField, Button, Box,
    Divider, Grid, MenuItem, Autocomplete, CircularProgress, Card, CardContent
} from '@mui/material';
import {
    Print as PrintIcon, Receipt as ReceiptIcon, Search as SearchIcon
} from '@mui/icons-material';

const Payslip = () => {
    const [staffList, setStaffList] = useState([]);
    const [schoolInfo, setSchoolInfo] = useState(null);
    const [schoolId, setSchoolId] = useState(null);
    const [loading, setLoading] = useState(true);

    // Filter States
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [payslipData, setPayslipData] = useState(null);

    const printRef = useRef();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (userDoc.exists()) {
                    const sId = userDoc.data().schoolId;
                    setSchoolId(sId);
                    fetchSchoolDetails(sId);
                    fetchStaffWithAllocations(sId);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const fetchSchoolDetails = async (sId) => {
        const sDoc = await getDoc(doc(db, "schools", sId));
        if (sDoc.exists()) setSchoolInfo(sDoc.data());
    };

    const fetchStaffWithAllocations = async (sId) => {
        // Fetch everyone who has a salary allocated
        const q = query(collection(db, "salaryAllocations"), where("schoolId", "==", sId));
        const snap = await getDocs(q);
        const list = snap.docs.map(doc => ({
            id: doc.id,
            staffId: doc.data().staffId,
            label: doc.data().staffName,
            ...doc.data()
        }));
        setStaffList(list);
    };

    const handleGenerate = () => {
        if (!selectedStaff) return alert("Please select a staff member");
        setPayslipData(selectedStaff);
    };

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Payslip_${selectedStaff?.staffName}_${month}_${year}`,
    });

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    if (loading) return <Box sx={{ textAlign: 'center', mt: 10 }}><CircularProgress /></Box>;

    return (
        <Grid  sx={{ mt: 1, justifyContent: 'center' }}>
            <Grid item xs={12} md={10}>
                {/* Selection Section */}
                <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Generate Staff Payslip</Typography>
                    <Grid className="row" spacing={2} alignItems="center">
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={5}>
                            <Autocomplete
                                options={staffList}
                                getOptionLabel={(option) => option.label}
                                value={selectedStaff}
                                onChange={(e, val) => setSelectedStaff(val)}
                                renderInput={(params) => <TextField {...params} label="Select Staff" size="small" />}
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={6} sm={2.5}>
                            <TextField select fullWidth label="Month" size="small" value={month} onChange={(e) => setMonth(e.target.value)}>
                                {months.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={6} sm={2}>
                            <TextField fullWidth label="Year" type="number" size="small" value={year} onChange={(e) => setYear(e.target.value)} />
                        </Grid>
                        <Grid className="col-sm-3 mb-3" item xs={12} sm={2.5}>
                            <Button fullWidth variant="contained" startIcon={<SearchIcon />} onClick={handleGenerate}>View Slip</Button>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Preview Section */}
                {payslipData && (
                    <Box sx={{ mb: 5 }}>
                        <Box sx={{ textAlign: 'right', mb: 2 }}>
                            <Button variant="outlined" color="secondary" startIcon={<PrintIcon />} onClick={handlePrint}>Print Payslip</Button>
                        </Box>

                        {/* Printable Area */}
                        <Card ref={printRef} sx={{ p: 4, border: '1px solid #ddd', boxShadow: 'none' }}>
                            <CardContent>
                                {/* Header */}
                                <Box sx={{ textAlign: 'center', mb: 3 }}>
                                    <Typography variant="h5" fontWeight="bold">{schoolInfo?.schoolName || "SCHOOL NAME"}</Typography>
                                    <Typography variant="body2">{schoolInfo?.address || "School Address Line"}</Typography>
                                    <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold', textDecoration: 'underline' }}>
                                        PAYSLIP FOR {months[month - 1].toUpperCase()} {year}
                                    </Typography>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                {/* Staff Info */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2"><b>Staff Name:</b> {payslipData.staffName}</Typography>
                                        <Typography variant="body2"><b>Staff ID:</b> {payslipData.staffId.slice(0, 8)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                        <Typography variant="body2"><b>Payment Mode:</b> {payslipData.paymentMode}</Typography>
                                        <Typography variant="body2"><b>Designation:</b> Staff</Typography>
                                    </Grid>
                                </Grid>

                                {/* Salary Table */}
                                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #eee' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f9f9f9' }}>
                                            <th style={{ padding: '10px', textAlign: 'left', border: '1px solid #eee' }}>Earnings</th>
                                            <th style={{ padding: '10px', textAlign: 'right', border: '1px solid #eee' }}>Amount (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ padding: '10px', border: '1px solid #eee' }}>Basic Salary</td>
                                            <td style={{ padding: '10px', textAlign: 'right', border: '1px solid #eee' }}>{payslipData.basic}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '10px', border: '1px solid #eee' }}>HRA (House Rent Allowance)</td>
                                            <td style={{ padding: '10px', textAlign: 'right', border: '1px solid #eee' }}>{payslipData.hra}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '10px', border: '1px solid #eee' }}>DA (Dearness Allowance)</td>
                                            <td style={{ padding: '10px', textAlign: 'right', border: '1px solid #eee' }}>{payslipData.da}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '10px', border: '1px solid #eee' }}>TA (Travel Allowance)</td>
                                            <td style={{ padding: '10px', textAlign: 'right', border: '1px solid #eee' }}>{payslipData.ta}</td>
                                        </tr>
                                        <tr style={{ fontWeight: 'bold', backgroundColor: '#f0f7ff' }}>
                                            <td style={{ padding: '10px', border: '1px solid #eee' }}>GROSS SALARY</td>
                                            <td style={{ padding: '10px', textAlign: 'right', border: '1px solid #eee' }}>₹{payslipData.totalSalary}</td>
                                        </tr>
                                    </tbody>
                                </table>

                                {/* Bank Details (If applicable) */}
                                {payslipData.paymentMode === 'Bank Transfer' && (
                                    <Box sx={{ mt: 3, p: 2, bgcolor: '#fcfcfc', border: '1px solid #eee' }}>
                                        <Typography variant="caption" fontWeight="bold">BANK DETAILS</Typography>
                                        <Grid container sx={{ mt: 1 }}>
                                            <Grid item xs={6}><Typography variant="caption">Bank: {payslipData.bankName}</Typography></Grid>
                                            <Grid item xs={6}><Typography variant="caption">A/C: {payslipData.accountNo}</Typography></Grid>
                                            <Grid item xs={6}><Typography variant="caption">IFSC: {payslipData.ifsc}</Typography></Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {/* Footer Signatures */}
                                <Grid container sx={{ mt: 10 }}>
                                    <Grid item xs={6}>
                                        <Divider sx={{ width: '150px', mb: 1 }} />
                                        <Typography variant="body2">Employee Signature</Typography>
                                    </Grid>
                                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                                        <Divider sx={{ width: '150px', ml: 'auto', mb: 1 }} />
                                        <Typography variant="body2">Principal / Accountant</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Grid>
        </Grid>
    );
};

export default Payslip;